import type {
  PageCatalogSettingsPermissions,
  PageDefinition,
} from './page_catalog_definitions.helpers';

export const PERSONAL_SETTINGS: readonly PageDefinition[] = [
  {
    id: 'devices',
    label: 'Devices',
    route: '/settings/devices',
    searchTerms: ['my devices', 'paired devices', 'device settings'],
  },
  {
    // Every workspace's shares in one list. Its visible title matches the
    // workspace's /drives/shared-with-me view, which is listed first and so
    // wins the "shared with me" tie.
    id: 'shared',
    label: 'Shared with me',
    route: '/shared',
    searchTerms: ['shared items', 'shared links'],
  },
  {
    // The workspace menu's "Settings" item.
    id: 'personal-settings',
    label: 'Personal settings',
    route: '/settings/profile',
    searchTerms: [
      'settings',
      'profile',
      'account settings',
      'my settings',
      'language',
      'change language',
      'app language',
      'workspace language',
      'regional settings',
    ],
    note: 'Language and regional settings are personal and live on this page. A workspace has no separate language setting.',
  },
  {
    id: 'preferences',
    label: 'Preferences',
    route: '/settings/preferences',
    searchTerms: ['appearance', 'assistant', 'notification settings'],
  },
  {
    id: 'personal-memories',
    label: 'Personal memories',
    route: '/settings/memories',
    searchTerms: ['my memories', 'personalization'],
  },
  {
    id: 'personal-summarization',
    label: 'Summarization settings',
    route: '/settings/summarization',
    searchTerms: ['summary', 'voice', 'speech'],
  },
  {
    id: 'appearance',
    label: 'Appearance settings',
    route: '/settings/appearance',
    searchTerms: ['theme', 'dark mode', 'haptics'],
  },
  {
    id: 'security',
    label: 'Security settings',
    route: '/settings/security',
    searchTerms: ['password', 'authentication', 'login'],
  },
  {
    id: 'my-workspaces',
    label: 'My workspaces',
    route: '/settings/organizations',
    searchTerms: ['organizations', 'switch workspace'],
  },
  {
    id: 'my-invitations',
    label: 'My invitations',
    route: '/settings/invitations',
    searchTerms: ['invites', 'join workspace'],
  },
];

export const WORKSPACE_SETTINGS: readonly (PageDefinition & {
  permission?: keyof PageCatalogSettingsPermissions;
})[] = [
  {
    id: 'workspace-settings',
    label: 'Workspace settings',
    route: '/settings/workspace',
    searchTerms: ['organization settings', 'workspace details', 'name', 'logo'],
  },
  {
    id: 'members',
    label: 'Members',
    route: '/settings/members',
    searchTerms: [
      'members',
      'invite',
      'invite members',
      'people',
      'team members',
    ],
  },
  {
    id: 'teams',
    label: 'Teams',
    route: '/settings/teams',
    searchTerms: ['groups', 'team access'],
  },
  {
    id: 'permissions',
    label: 'Permissions',
    route: '/settings/permissions',
    searchTerms: ['role permissions', 'access control'],
  },
  {
    id: 'roles',
    label: 'Roles',
    route: '/settings/roles',
    searchTerms: ['member role', 'member roles', 'role settings'],
  },
  {
    id: 'app-permissions',
    label: 'App permissions',
    route: '/settings/app-permissions',
    searchTerms: [
      'app permissions',
      'always allow',
      'consents',
      'revoke',
      'app access',
    ],
  },
  {
    id: 'connection-access',
    label: 'Connection access',
    route: '/settings/connection-access',
    searchTerms: [
      'connection access',
      'admin access',
      'grants',
      'gmail',
      'slack',
    ],
    note: "Workspace admin controls for members' connected-app access. It does not show anyone's email, calendar, or messages.",
  },
  {
    id: 'analytics',
    label: 'Analytics',
    route: '/settings/analytics',
    // "Activity" is the sidebar's notifications page.
    searchTerms: ['workspace activity', 'insights', 'metrics', 'reports'],
  },
  {
    id: 'workspace-memories',
    label: 'Workspace memories',
    route: '/settings/memories',
    searchTerms: ['shared knowledge', 'workspace context'],
  },
  {
    id: 'file-permissions',
    label: 'File permissions',
    route: '/settings/file-permissions',
    searchTerms: ['filesystem permissions', 'folder access'],
    permission: 'showFilePermissions',
  },
  {
    id: 'storage',
    label: 'Storage settings',
    route: '/settings/storage',
    searchTerms: ['storage limits', 'files used'],
    permission: 'canViewStorageLimits',
  },
  {
    id: 'plans',
    label: 'Plans and credits',
    route: '/settings/purchase',
    // The workspace menu's "Pricing" item.
    searchTerms: [
      'pricing',
      'plans',
      'credits',
      'subscription',
      'upgrade',
      'top up',
    ],
    permission: 'canManageBilling',
  },
  {
    id: 'usage',
    label: 'Usage',
    route: '/settings/usage',
    searchTerms: ['credit usage', 'credits used', 'usage history', 'spending'],
  },
  {
    id: 'billing',
    label: 'Billing',
    route: '/settings/billing',
    searchTerms: ['invoice', 'payment', 'receipt'],
    permission: 'canViewBilling',
  },
  {
    id: 'archived-chats',
    label: 'Archived chats',
    route: '/settings/archived-chats',
    searchTerms: ['archive', 'restore chats'],
  },
];
